<script>
import { mapActions, mapGetters } from 'vuex';
import axios from 'axios';
import moment from 'moment';
import Vue from 'vue';
import Donut from 'vue-css-donut-chart';
import 'vue-css-donut-chart/dist/vcdonut.css';
import CompareStatusPanel from '@/components/CompareStatusPanel.vue';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import BreadcrumbsManager from '@/mixins/BreadcrumbManager';
import NotificationManager from '@/mixins/NotificationManager';
import StateIdUtils from '@/mixins/StateIdUtils';
import FieldLabels from '@/mixins/FieldLabels';

Vue.use(Donut);

const matchTypeMap = {
  FILE: 'FILE',
  MANUAL: 'MANL',
  DM_ALL_STUDENTS: 'DMALL',
  ADDRESS: 'ADDR',
  DM_DISTRICT: 'DMDIS',
  INDIVIDUAL: 'INDIV',
  EXTERNAL: 'EXTRN',
};

export default {
  mixins: [
    BreadcrumbsManager,
    NotificationManager,
    FieldLabels,
    StateIdUtils,
  ],
  components: {
    CompareStatusPanel,
    EsException,
    EsHeader,
    EsLoader,
  },
  props: {
    nearMatchId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      batchPendingCount: null,
      dmKeys: [],
      dmTransStatus: {},
      isCancelRecord: false,
      isMatch: false,
      isNoMatch: false,
      loading: false,
      loadingError: false,
      messages: [],
      nearMatches: {},
      nearMatchDistrictCode: null,
      nearMatchTranId: null,
      showModal: false,
      students: [],
      userPersonTypesCodes: null,
    };
  },
  async created() {
    await this.onLoad();
    this.dmKeys = this.getObjectKeys('MATCHING');
  },
  computed: {
    ...mapGetters(['userPersonTypes']),
    ...mapGetters('propertiesModule', [
      'properties',
      'getProperty',
    ]),
    ...mapGetters({
      hasDistrictSchoolPermission: 'hasDistrictSchoolPermission',
    }),
    ...mapGetters('lookups', [
      'activeGenders',
      'activeSources',
      'activeStates',
      'activeGrades',
      'activePrograms',
      'activeMatchTypes',
      'activeRaces',
      'activeEthnicities',
      'defaultPersonType',
    ]),
    ...mapGetters('fieldLabelsModule', [
      'getFieldLabelsByGroup',
      'getFieldLabelByPersonType',
    ]),
    assignedStateId() {
      if (this.isResolvedMatch) {
        return this.nearMatches.dmMatchResolutionTrans.dmMatchTransDetail.assignedStateId;
      }
      return null;
    },
    batchId() {
      if (this.nearMatches.response.request.dmBatchId) {
        return this.nearMatches.response.request.dmBatchId;
      }
      return null;
    },
    isCanceled() {
      if (this.dmTransStatus.code === '13') {
        return true;
      }
      return null;
    },
    isResolvedNoMatch() {
      if (this.nearMatches.dmMatchResolutionTrans
        && this.dmTransStatus.code === '15') {
        return true;
      }
      return null;
    },
    isResolvedMatch() {
      if (this.nearMatches.dmMatchResolutionTrans
        && this.dmTransStatus.code === '14') {
        return true;
      }
      return false;
    },
    isPending() {
      return !this.isCanceled && !this.isResolvedMatch && !this.isResolvedNoMatch;
    },
    decisionStatus() {
      if (this.isCanceled) {
        return 'Canceled';
      } if (this.isResolvedMatch || this.isResolvedNoMatch) {
        return 'Resolved';
      } if (this.isPending) {
        return 'Pending';
      }
      return null;
    },
    matchStatusPanelIcon() {
      let icon;
      if (this.isResolvedMatch || this.isResolvedNoMatch) {
        icon = 'check-circle';
      } else if (this.isCanceled) {
        icon = 'window-close';
      } else if (this.isPending) {
        icon = 'exclamation-circle';
      }
      return ['fas', icon];
    },
    stateBatch() {
      if (this.nearMatches) {
        return this.nearMatches.response.request.matchType === 'DM_ALL_STUDENTS';
      }
      return false;
    },
    districtBatch() {
      if (this.nearMatches) {
        return this.nearMatches.response.request.matchType === 'DM_DISTRICT';
      }
      return false;
    },
    matchStatusIcon() {
      let icon;
      if (this.isResolvedMatch) {
        icon = 'check-circle';
      } else if (this.isResolvedNoMatch) {
        icon = 'stop-circle';
      } else if (this.isCanceled) {
        icon = 'window-close';
      } else if (this.isPending) {
        icon = 'exclamation-circle';
      }
      return ['fas', icon];
    },
    matchStatusIconColor() {
      let color;
      if (this.decisionStatus === 'Canceled') {
        color = '#B5B5B5';
      } else if (this.decisionStatus === 'Resolved') {
        color = '#28A745';
      } else if (this.decisionStatus === 'Pending') {
        color = '#FFC107';
      }
      return `color: ${color}`;
    },
    mappedMatchType() {
      const code = matchTypeMap[this.nearMatches.response.request.matchType];
      return this.activeMatchTypes.find(m => m.code === code).description;
    },
    personType() {
      if (this.userPersonTypesCodes.includes(this.defaultPersonType)) {
        return this.defaultPersonType;
      } return this.userPersonTypesCodes[0];
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadGenders',
      'loadSources',
      'loadGrades',
      'loadStates',
      'loadPrograms',
      'loadMatchTypes',
      'loadRaces',
      'loadPersonTypes',
      'loadEthnicities',
    ]),
    ...mapActions(['getStudent']),
    assignedStateIdResolution(id) {
      if (this.assignedStateId === id) {
        return `Matched ID ${id}`;
      } if (this.isCanceled) {
        return 'Canceled';
      }
      return 'No Match';
    },
    async reloadPage() {
      try {
        this.loading = true;
        this.loadingError = false;
        const res = await Promise.all([
          this.getMatches(),
          this.getTransaction(),
        ]);
        if (res[0].data.response.request.dmBatchId) {
          const batchRes = await this.getBatch(res[0].data.response.request.dmBatchId);
          this.batchPendingCount = batchRes.data.pendingCount;
        }
        this.nearMatches = res[0].data;
        this.dmTransStatus = res[1].data.dmTransStatus;
        const { potentialMatches } = this.nearMatches.response;
        if (potentialMatches.length > 0) {
          potentialMatches.forEach(async (p, i) => {
            potentialMatches[i].matchedObject = this.flattenTheRecord(p.matchedObject);
            if (this.stateBatch) {
              const response = await this.getStudent(
                potentialMatches[i].matchedObject.uniqueidentifer,
              );
              this.students.push(response.data);
            }
          });
        }
        this.nearMatches.response.potentialMatches = potentialMatches;
        this.nearMatches.response.request.target.uncompressedTherecord = this.flattenTheRecord(
          this.nearMatches.response.request.target.uncompressedTherecord,
        );
      } catch (e) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    async onLoad() {
      this.loading = true;
      this.loadingError = false;
      this.setBreadcrumb([
        { text: 'Home', to: { name: 'directMatch' } },
        { text: `Compare ${this.properties['SYSTEM_LABEL-NEARMATCH']}`, to: { } },
      ]);
      try {
        const res = await Promise.all([
          this.loadGenders(),
          this.loadSources(),
          this.loadRaces(),
          this.loadGrades(),
          this.loadStates(),
          this.getMatches(),
          this.getTransaction(),
          this.loadPrograms(),
          this.loadMatchTypes(),
          this.loadPersonTypes(),
          this.loadEthnicities(),
        ]);
        if (res[5].data.response.request.dmBatchId) {
          const batchRes = await this.getBatch(res[5].data.response.request.dmBatchId);
          this.batchPendingCount = batchRes.data.pendingCount;
        }
        this.nearMatches = res[5].data;
        this.dmTransStatus = res[6].data.dmTransStatus;
        const { potentialMatches } = this.nearMatches.response;
        if (potentialMatches.length > 0) {
          potentialMatches.forEach(async (p, i) => {
            potentialMatches[i].matchedObject = this.flattenTheRecord(p.matchedObject);
            if (this.stateBatch) {
              const response = await this.getStudent(
                potentialMatches[i].matchedObject.uniqueidentifer,
              );
              this.students.push(response.data);
            }
          });
        }
        this.nearMatches.response.potentialMatches = potentialMatches;
        this.nearMatches.response.request.target.uncompressedTherecord = this.flattenTheRecord(
          this.nearMatches.response.request.target.uncompressedTherecord,
        );
      } catch (error) {
        this.apiMsg = `${error.response.status === 500
          ? 'Network Error'
          : error.response.data.message}`;
        this.messages.push(this.apiMsg);
        this.loadingError = true;
        this.hasError = true;
        this.showMessage();
      }
      this.loading = false;
      this.userPersonTypesCodes = this.userPersonTypes
        .map(item => item.code);
    },
    getBatch(batchId) {
      return axios.get(`/directmatch/batches/${batchId}`);
    },
    getStudentSourceCode(studentId) {
      let findSourceCode = null;
      if (this.students) {
        findSourceCode = this.students.find(s => s.stateStudentId === studentId);
        if (findSourceCode) {
          return findSourceCode.sourceCode;
        }
      }
      return '';
    },
    getObjectKeys(group) {
      const arr = this.getFieldLabelsByGroup(group).map(l => l.code.toLowerCase());
      return [...new Set(arr)];
    },
    getFieldLabelsForObjectKeys(code, group) {
      const dm = this.getFieldLabelByPersonType(code.toUpperCase(), group, this.personType);
      return dm;
    },
    isAssignedStateId(stateId) {
      if (this.assignedStateId && this.assignedStateId === stateId) {
        return true;
      }
      return false;
    },
    showMessage() {
      const type = this.hasError ? 'danger' : 'success';
      this.showNotification(type, this.messages);
    },
    closeModal() {
      this.showModal = false;
      this.isCancelRecord = false;
      this.isNoMatch = false;
      this.isMatch = false;
    },
    openModalForCancel() {
      this.showModal = true;
      this.isCancelRecord = true;
    },
    openModalForNoMatch() {
      this.showModal = true;
      this.isNoMatch = true;
    },
    openModalForMatch(idForAssign, districtCode) {
      this.showModal = true;
      this.isMatch = true;
      this.nearMatchTranId = idForAssign;
      this.nearMatchDistrictCode = districtCode;
    },
    formatSsn(ssn) {
      if (ssn) {
        if (!this.hasDistrictSchoolPermission(this.matchPermission('DRMCH:PRPRF:SSN:VIEW'))) {
          return this.maskSsn(ssn);
        }
        const numbers = ssn.replace(/[^0-9]/g, '');
        if (numbers.length === 9) {
          return `${numbers.substr(0, 3)}-${numbers.substr(3, 2)}-${numbers.substr(5, 4)}`;
        }
      }
      return '\u00A0';
    },
    maskSsn(ssn) {
      const maskedSSN = ssn.split('-').map(part => part.replace(/\d/g, '#')).join('-');
      return `${maskedSSN.substr(0, 3)}-${maskedSSN.substr(3, 2)}-${maskedSSN.substr(5, 4)}`;
    },
    async deleteMatch() {
      try {
        this.closeModal();
        await axios.put(`/directmatch/transactions/${this.nearMatches.response.requestId}`,
          { command: 'CANCEL', districtCode: this.nearMatches.response.districtCode });
        // DM_NEAR_MATCH_RESOLVE_BY_CANCEL
        this.reloadPage();
      } catch {
        // handle
      }
    },
    async match(idForAssign, districtCode) {
      try {
        this.closeModal();
        await axios.put(`/directmatch/transactions/${this.nearMatches.response.requestId}`,
          { command: 'MATCH', matchIdForAssign: idForAssign, districtCode });
        // DM_NEAR_MATCH_RESOLVE_BY_MATCH
        this.reloadPage();
      } catch {
        // handle
      }
    },
    async notMatch() {
      try {
        this.closeModal();
        await axios.put(`/directmatch/transactions/${this.nearMatches.response.requestId}`,
          { command: 'NO_MATCH', districtCode: this.nearMatches.response.districtCode });
        // DM_NEAR_MATCH_RESOLVE_BY_NO_MATCH
        this.reloadPage();
      } catch {
        // handle error
      }
    },
    compareDate(str1, str2) {
      const date1 = moment(str1).format('MM/DD/YYYY');
      const date2 = moment(str2).format('MM/DD/YYYY');
      if (date1 !== date2) {
        return true;
      }
      return false;
    },
    compareFields(submission, nearMatch) {
      let sub = submission || null;
      let nm = nearMatch || null;

      if (typeof sub === 'string') {
        sub = sub.toLowerCase();
      }
      if (typeof nm === 'string') {
        nm = nm.toLowerCase();
      }

      return sub !== nm;
    },
    mapTitle(title) {
      if (this.titles && title) {
        const description = this.titles.find(x => x.code === title);
        return description.description;
      }
      return 'N/A';
    },
    nearMatchedObject(value) {
      return this.stateBatch ? value.matchedObject : value.matchedObject.uncompressedTherecord;
    },
    toggleLoading() {
      this.loading = !this.loading;
    },
    getMatches() {
      return axios.get(`/directmatch/transactions/${this.nearMatchId}/details`);
    },
    getTransaction() {
      return axios.get(`/directmatch/transactions/${this.nearMatchId}`);
    },
    flattenTheRecord(values) {
      const record = {};
      [values].forEach((e) => {
        Object.keys(e).forEach((key) => { record[key] = e[key]; });
      });
      return record;
    },
    mapProgram(code) {
      return this.activePrograms.find(p => p.code === code).description;
    },
    matchPermission(permission) {
      return {
        permission,
        districtCode: this.nearMatches.response.districtCode,
        schoolCode: this.nearMatches.response.schoolCode,
      };
    },
    getPersonType(value) {
      if (!this.dmPersonTypeMatching()) {
        return 'N/A';
      }
      return this.getPersonTypeName(value);
    },
    dmPersonTypeMatching() {
      return JSON.parse(this.getProperty('MATCHING_FEATURES-FEATURE_DM_PERSONTYPE_MATCHING'));
    },
    getPersonTypeName(value) {
      if (value) {
        const item = this.userPersonTypes.find(g => g.code === value);
        if (item) {
          return item.name;
        } return 'N/A';
      }
      return '\u00A0';
    },
  },
  filters: {
    getDescripton(value, list) {
      if (value) {
        const item = list.find(g => g.code === value);
        if (item) {
          return item.description;
        }
      }
      return value;
    },
    getName(value, list) {
      if (value) {
        const item = list.find(g => g.code === value);
        if (item) {
          return item.name;
        }
      }
      return '\u00A0';
    },
    OrPlaceHolder(value) {
      if (value) {
        return value;
      }
      return '\u00A0';
    },
  },
};
</script>

<template>
  <div class="document-container">
    <es-loader v-if="loading"/>
    <es-exception v-else-if="loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
    />
    <div v-else>
      <div v-if="showModal" class="modal is-active">
        <div class="modal-background"></div>
        <div class="modal-card">
          <header class="modal-card-head">
            <p
              v-if="isCancelRecord"
              class="modal-card-title has-text-weight-bold"
            >
              Cancel Submission Record
            </p>
            <p v-else class="modal-card-title has-text-weight-bold">
              Confirm Decision
            </p>
            <button
              class="delete"
              aria-label="close"
              @click="closeModal()"
            >
            </button>
          </header>
          <section class="modal-card-body" v-if="isCancelRecord">
            <div class="modal-body has-text-left">
              Are you sure you want to cancel this submission record?
            </div>
            <br><br>
            <div class="is-flex flex-justify--end flex-align--center">
              <a @click="closeModal()">
                Cancel
              </a>
              <button
                class="button is-primary margin-left"
                @click="deleteMatch()"
              >
                Cancel Submission Record
              </button>
            </div>
          </section>
          <section class="modal-card-body" v-if="isNoMatch">
            <div class="modal-body has-text-left">
              Are you sure you want to make a
              "{{ properties['BUTTON_LABEL-CREATE_NEW'] }}" decision for the submission record?
            </div>
            <br><br>
            <div class="is-flex flex-justify--end flex-align--center">
              <a @click="closeModal()">
                Cancel
              </a>
              <button
                class="button is-primary margin-left"
                @click="notMatch()"
              >
                Yes Select {{ properties['BUTTON_LABEL-CREATE_NEW'] }}
              </button>
            </div>
          </section>
          <section class="modal-card-body" v-if="isMatch">
            <div class="modal-body has-text-left">
              Are you sure you want to make a
              "{{ properties['BUTTON_LABEL-ASSIGN_EXISTING'] }}" decision for the submission record?
            </div>
            <br><br>
            <div class="is-flex flex-justify--end flex-align--center">
              <a @click="closeModal()">
                Cancel
              </a>
              <button
                class="button is-primary margin-left"
                @click="match(nearMatchTranId, nearMatchDistrictCode)"
              >
                Yes Select {{ properties['BUTTON_LABEL-ASSIGN_EXISTING'] }}
              </button>
            </div>
          </section>
        </div>
      </div>
      <div class="document">
        <div class="header">
          <es-header>
            <template #header>
              <div class="header-title">
                Compare Records
              </div>
              <div class="header-subtitle">
                The {{ properties['SYSTEM_LABEL-NEARMATCH'] }}(s/es)
                for the submission record are displayed below.
              </div>
            </template>
            <template #actions>
                <ul>
                  <li class="p-narrow list-content" v-if="stateBatch">
                    <div class="list-header">
                      <div class="">
                        <figure class="image is-32x32 stateImage">
                          <img src="@/assets/state.png">
                        </figure>
                      </div>
                      <div class="has-text-grey" style="font-size: 10px">
                        <div class="">
                          STATE BATCH
                        </div>
                      </div>
                    </div>
                  </li>
                  <li class="p-narrow list-content">
                    <div class="list-header">
                      <div class="list-title">
                        {{ mapProgram(nearMatches.response.request.programCode) }}
                      </div>
                      <div class="list-subtitle">
                        Match Type
                      </div>
                    </div>
                  </li>
                  <li class="p-narrow list-content">
                    <div class="list-header">
                      <div class="list-title">
                        {{ batchId || '--'}}
                      </div>
                      <div class="list-subtitle">
                        Batch ID
                      </div>
                    </div>
                  </li>
                  <li class="p-narrow list-content">
                    <div class="list-header">
                      <div class="list-title">
                        {{ nearMatches.response.requestId }}
                      </div>
                      <div class="list-subtitle">
                        Transaction ID
                      </div>
                    </div>
                  </li>
                  <li class="p-narrow list-content">
                    <div class="list-header is-flex flex-align--center"
                      :style="matchStatusIconColor"
                      style="font-size: 25px">
                      <font-awesome-icon
                        :icon="matchStatusPanelIcon"
                      />
                      <span class="list-title" style="padding-left: 0.5rem">
                        {{ decisionStatus }}
                      </span>
                    </div>
                  </li>
                </ul>
            </template>
          </es-header>
        </div>
        <div class="padding--1rem">
          <div class="" style="max-width: 1200px; margin: auto;">
            <compare-status-panel
              style="max-width: 1200px; margin: auto; margin-bottom: 1rem;"
              :batch-id="batchId"
              :decision-status="decisionStatus"
              :is-resolved-no-match="isResolvedNoMatch"
              :is-resolved-match="isResolvedMatch"
              :match-status-icon="matchStatusIcon"
              :match-status-panel-icon="matchStatusPanelIcon"
              :match-status-icon-color="matchStatusIconColor"
              :near-matches="nearMatches"
              :near-match-id="nearMatchId"
              :pending-count="batchPendingCount"
            />
            <div
              v-if="isPending"
              class="field is-grouped is-flex flex-justify--end"
            >
              <div
                v-if="hasDistrictSchoolPermission(
                  matchPermission('DRMCH:MATCH:RESOLVENM:CANCEL')
                )"
                class="control is-flex flex-align--center"
              >
                <a @click="openModalForCancel()">
                  Cancel Submission Record
                </a>
              </div>
              <div
                v-if="hasDistrictSchoolPermission(
                  matchPermission('DRMCH:MATCH:RESOLVENM:NOMATCH')
                )"
                class="control"
              >
                <button class="button is-link is-light" @click="openModalForNoMatch()">
                  {{ properties['BUTTON_LABEL-CREATE_NEW'] }}
                </button>
              </div>
            </div>
          </div>
          <div class="outer">
            <div class="inner">
              <table class="table is-fullwidth">
                <tbody>
                  <tr>
                    <td class="compare-panel vertical-align--initial">
                      <div class="text-align--center">
                        SUBMISSION RECORD
                      </div>
                      <div class="text-align--center">
                        <div class="source-label has-text-weight-bold capitalize" v-if="stateBatch">
                          Source: {{ mapProgram(nearMatches.response.request.programCode) }}
                        </div>
                        <div class="source-label has-text-weight-bold" v-else-if="districtBatch">
                          Source: Person ID
                        </div>
                        <div class="source-label has-text-weight-bold" v-else>
                          Source: {{ nearMatches.response.request.matchType.charAt(0).toUpperCase()
                                + nearMatches.response.request.matchType.slice(1).toLowerCase() }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel border--left"
                      :class="{ 'assigned-state-id': isAssignedStateId(potentialMatch.id) }"
                    >
                    <!-- :class="{ assigned-state-id: isAssignedStateId(potentialMatch) }" -->
                      <div class="is-flex flex-direction--column flex-align--center" >
                        <div class="is-size-8 is-uppercase">
                          {{ properties['SYSTEM_LABEL-NEARMATCH'] }} RECORD
                        </div>
                        <div class="text-align--center margin-bottom--half">
                          <div class="source-label has-text-weight-bold" v-if="stateBatch">
                            Source: Person ID
                          </div>
                          <div class="source-label has-text-weight-bold" v-else>
                            Source: {{ mapProgram(nearMatches.response.request.programCode) }}
                          </div>
                        </div>
                        <div class="">
                          <vc-donut
                            class="margin-bottom--half"
                            :size="60"
                            :sections="[
                            { value: potentialMatch.percent - 1, color: '#215E97' },
                            { value: 1, color: '#FFFFFF' },
                            { value: 99 - potentialMatch.percent, color: '#C8C8C8'},
                            { value: 1, color: '#FFFFFF' },]"
                          >
                            <h1 class="percent-label">
                              {{ potentialMatch.percent }}%
                            </h1>
                          </vc-donut>
                          <div class="has-text-grey margin-bottom--half">
                            Match Score
                          </div>
                        </div>
                        <span
                          v-if="!isPending"
                          class="has-text-weight-bold"
                          style="font-size: 16px"
                        >
                          {{ assignedStateIdResolution(potentialMatch.id) }}
                        </span>
                        <button
                          v-else-if="hasDistrictSchoolPermission(
                            matchPermission('DRMCH:MATCH:RESOLVENM:MATCHDEC')
                          )"
                          class="button is-primary"
                          @click="openModalForMatch(
                            potentialMatch.id,
                            nearMatchedObject(potentialMatch).districtCode
                          )"
                        >
                          {{ properties['BUTTON_LABEL-ASSIGN_EXISTING'] }}: ID&nbsp;
                          <span v-if="stateBatch">{{ formatStateID(potentialMatch.id) }}</span>
                          <span v-else>{{ potentialMatch.id }}</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--heading has-text-weight-bold is-size-8">
                      Matching Fields
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--heading has-text-weight-bold is-size-8"
                      :class="{ 'assigned-state-id': isAssignedStateId(potentialMatch.id) }"
                    >
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:FIRST_NAME`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.firstName }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted':
                          compareFields(nearMatches.response.request.target.firstName,
                          nearMatchedObject(potentialMatch).firstName),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).firstName }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:MIDDLE_NAME`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.middleName | OrPlaceHolder }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.middleName,
                          nearMatchedObject(potentialMatch).middleName
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id)
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).middleName | OrPlaceHolder }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:LAST_NAME`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.lastName }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.lastName,
                          nearMatchedObject(potentialMatch).lastName
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).lastName }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:SUFFIX`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.suffix | OrPlaceHolder }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.suffix,
                          nearMatchedObject(potentialMatch).suffix
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).suffix | OrPlaceHolder }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                  {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:PREVIOUS_LAST_NAME`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.previousName | OrPlaceHolder }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.previousName,
                          nearMatchedObject(potentialMatch).previousName
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).previousName | OrPlaceHolder }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:GENDER_CODE`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.genderCode
                            | getDescripton(activeGenders) }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.genderCode,
                          nearMatchedObject(potentialMatch).genderCode
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).genderCode
                          | getDescripton(activeGenders) }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:DATE_OF_BIRTH`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.dateOfBirth
                            | formatDateMMDDYYYY }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareDate(
                          nearMatches.response.request.target.dateOfBirth,
                          nearMatchedObject(potentialMatch).dateOfBirth
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).dateOfBirth | formatDateMMDDYYYY }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:SOCIAL_SECURITY_NUMBER`) }}
                        </div>
                        <div class="compare--content">
                          {{ formatSsn(nearMatches.response.request.target.ssn) | OrPlaceHolder }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.ssn,
                          stateBatch
                            ? potentialMatch.matchedObject.ssn
                            : potentialMatch.matchedObject
                              .uncompressedTherecord.socialSecurityNumber
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        <template v-if="stateBatch">
                          <span v-if="potentialMatch.matchedObject.ssn">
                            {{ formatSsn(potentialMatch.matchedObject.ssn) | OrPlaceHolder }}
                          </span>
                        </template>
                        <template v-else>
                          <span v-if="potentialMatch.matchedObject
                            .uncompressedTherecord.socialSecurityNumber">
                            {{ formatSsn(potentialMatch.matchedObject
                              .uncompressedTherecord.socialSecurityNumber)
                              | OrPlaceHolder }}
                          </span>
                        </template>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--heading has-text-weight-bold is-size-8">
                      Person Information
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--heading has-text-weight-bold is-size-8"
                      :class="{ 'assigned-state-id': isAssignedStateId(potentialMatch.id) }"
                    >
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:ETHNICITY_INDICATOR`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.ethnicityIndicator
                            | getDescripton(activeEthnicities) }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.ethnicityIndicator,
                          nearMatchedObject(potentialMatch).ethnicityIndicator
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).ethnicityIndicator
                          | getDescripton(activeEthnicities) }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                        {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:RACE_ETHNIC_CODE`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.raceEthnicCode
                            | getDescripton(activeRaces) }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.raceEthnicCode,
                          nearMatchedObject(potentialMatch).raceEthnicCode
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).raceEthnicCode
                          | getDescripton(activeRaces) }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:RACE2_CODE`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.race2Code
                            | getDescripton(activeRaces)}}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.race2Code,
                          nearMatchedObject(potentialMatch).race2Code
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).race2Code
                          | getDescripton(activeRaces) }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:RACE3_CODE`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.race3Code
                            | getDescripton(activeRaces) }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.race3Code,
                          nearMatchedObject(potentialMatch).race3Code
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).race3Code
                          | getDescripton(activeRaces) }}
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:RACE4_CODE`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.race4Code
                            | getDescripton(activeRaces) }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.race4Code,
                          nearMatchedObject(potentialMatch).race4Code
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).race4Code
                          | getDescripton(activeRaces) }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:RACE5_CODE`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.race5Code
                            | getDescripton(activeRaces) }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.race5Code,
                          nearMatchedObject(potentialMatch).race5Code
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).race5Code
                          | getDescripton(activeRaces) }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                      {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:ETHNIC_SUB_GROUP`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.ethnicSubGroup | OrPlaceHolder }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.ethnicSubGroup,
                          nearMatchedObject(potentialMatch).ethnicSubGroup
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).ethnicSubGroup | OrPlaceHolder }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--heading has-text-weight-bold is-size-8">
                      Contact Information
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--heading has-text-weight-bold is-size-8"
                      style="text-align:center"
                      :class="{ 'assigned-state-id': isAssignedStateId(potentialMatch.id) }"
                    >
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:STREET_1`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.street1 | OrPlaceHolder }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.street1,
                          nearMatchedObject(potentialMatch).street1
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).street1 | OrPlaceHolder }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:STREET_2`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.street2 | OrPlaceHolder }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.street2,
                          nearMatchedObject(potentialMatch).street2
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).street2 | OrPlaceHolder }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:CITY`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.city | OrPlaceHolder }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.city,
                          nearMatchedObject(potentialMatch).city
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).city | OrPlaceHolder }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:STATE_CODE`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.stateCode
                            | getDescripton(activeStates) }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.stateCode,
                          nearMatchedObject(potentialMatch).stateCode
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).stateCode
                          | getDescripton(activeStates) }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:ZIP`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.zip | OrPlaceHolder }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.zip,
                          nearMatchedObject(potentialMatch).zip
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).zip | OrPlaceHolder }}
                      </div>
                    </td>
                  </tr>

                  <tr>
                    <td class="compare-panel compare-panel--heading has-text-weight-bold is-size-8">
                      Location Information
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--heading has-text-weight-bold is-size-8"
                      style="text-align:center"
                      :class="{ 'assigned-state-id': isAssignedStateId(potentialMatch.id) }"
                    >
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          Person Type
                        </div>
                        <div class="compare--content">
                          {{ getPersonType(
                            nearMatches.response.request.target.uncompressedTherecord.personType) }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          getPersonType(nearMatches.response.request.target.uncompressedTherecord
                            .personType),
                          getPersonType(nearMatchedObject(potentialMatch).personType)
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ getPersonType(nearMatchedObject(potentialMatch).personType)  }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:SCHOOL_YEAR`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.schoolYear | OrPlaceHolder }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.schoolYear,
                          stateBatch
                            ? nearMatchedObject(potentialMatch).schoolYear
                            : nearMatchedObject(potentialMatch)._schoolyear
                          ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        <template v-if="stateBatch">
                          <span v-if="nearMatchedObject(potentialMatch).schoolYear">
                            {{ nearMatchedObject(potentialMatch).schoolYear | OrPlaceHolder }}
                          </span>
                        </template>
                        <template v-else>
                          <span v-if="nearMatchedObject(potentialMatch)._schoolyear">
                            {{ nearMatchedObject(potentialMatch)._schoolyear | OrPlaceHolder }}
                          </span>
                        </template>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                  {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:DISTRICT_CODE`) }} Code
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.districtCode | OrPlaceHolder }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.districtCode,
                          nearMatchedObject(potentialMatch).districtCode
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).districtCode | OrPlaceHolder }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                    {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:SCHOOL_CODE`) }} Code
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.schoolCode | OrPlaceHolder }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.schoolCode,
                          nearMatchedObject(potentialMatch).schoolCode
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).schoolCode | OrPlaceHolder }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                    {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:RES_DISTRICT_CODE`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.resDistrictCode | OrPlaceHolder }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.resDistrictCode,
                          nearMatchedObject(potentialMatch).resDistrictCode
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).resDistrictCode | OrPlaceHolder }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                    {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:LOCAL_STUDENT_ID`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.localcode | OrPlaceHolder }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.localcode,
                          nearMatchedObject(potentialMatch).localcode
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).localcode }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:GRADE_CODE`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.gradeCode
                            | getDescripton(activeGrades) }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.gradeCode,
                          nearMatchedObject(potentialMatch).grade
                        ),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        {{ nearMatchedObject(potentialMatch).grade | getDescripton(activeGrades) }}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabel(`PER:${personType}:DIRECTMATCH:SOURCE_CODE`) }}
                        </div>
                        <div class="compare--content">
                          {{ nearMatches.response.request.target.sourceCode
                            | getDescripton(activeSources) }}
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{
                        'panel--highlighted': compareFields(
                          nearMatches.response.request.target.sourceCode,
                          stateBatch
                        ? getStudentSourceCode(nearMatchedObject(potentialMatch).uniqueidentifer)
                        : nearMatchedObject(potentialMatch).sourceCode),
                        'assigned-state-id': isAssignedStateId(potentialMatch.id),
                      }"
                    >
                      <div class="compare--content">
                        <template v-if="stateBatch">
                          <span v-if="getStudentSourceCode(
                              nearMatchedObject(potentialMatch).uniqueidentifer
                            )"
                          >
                        {{ getStudentSourceCode(nearMatchedObject(potentialMatch).uniqueidentifer)
                              | getDescripton(activeSources) }}
                          </span>
                        </template>
                        <template v-else>
                          <span v-if="nearMatchedObject(potentialMatch).sourceCode">
                            {{ nearMatchedObject(potentialMatch).sourceCode
                              | getDescripton(activeSources) }}
                          </span>
                        </template>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="compare-panel compare-panel--heading has-text-weight-bold is-size-8">
                      DirectMatch
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel compare-panel--heading has-text-weight-bold is-size-8"
                      :class="{ 'assigned-state-id': isAssignedStateId(potentialMatch.id) }"
                      style="text-align:center"
                    >
                    </td>
                  </tr>
                  <tr v-for="(dm, index) in getObjectKeys('MATCHING')" :key="dm">
                    <td class="compare-panel compare-panel--cell">
                      <div class="">
                        <div class="submission-header has-text-left">
                          {{ getFieldLabelsForObjectKeys(dm, 'MATCHING') }}
                        </div>
                        <div class="compare--content">
                          <template v-if="nearMatches.response.request.target
                            .uncompressedTherecord">
                            {{ nearMatches.response.request.target
                            .uncompressedTherecord[dmKeys[index]] | OrPlaceHolder }}
                          </template>
                        </div>
                      </div>
                    </td>
                    <td
                      v-for="(potentialMatch, nmIndex) in nearMatches.response.potentialMatches"
                      :key="nmIndex"
                      class="compare-panel compare-panel--cell border--left vertical-align--bottom"
                      :class="{ 'assigned-state-id': isAssignedStateId(potentialMatch.id) }"
                    >
                      <div class="compare--content">
                        <template v-if="nearMatchedObject(potentialMatch)">
                          {{ nearMatchedObject(potentialMatch)[dmKeys[index]] | OrPlaceHolder }}
                        </template>
                      </div>
                    </td>
                  </tr>
                  <tr
                    v-if="hasDistrictSchoolPermission(
                      matchPermission('DRMCH:MATCH:RESOLVENM:MATCHDEC')
                    )"
                  >
                    <td class="compare--content">
                      &nbsp;
                    </td>
                    <td
                      v-for="(potentialMatch, index) in nearMatches.response.potentialMatches"
                      :key="index"
                      class="compare-panel border--left text-align--center"
                      :class="{ 'assigned-state-id': isAssignedStateId(potentialMatch.id) }"
                    >
                      <span
                        v-if="!isPending"
                        class="has-text-weight-bold"
                        style="font-size: 16px"
                      >
                        {{ assignedStateIdResolution(potentialMatch.id) }}
                      </span>
                      <button
                        v-else
                        class="button is-primary"
                        @click="openModalForMatch(
                          potentialMatch.id,
                          nearMatchedObject(potentialMatch).districtCode
                        )"
                      >
                        {{ properties['BUTTON_LABEL-ASSIGN_EXISTING'] }}: ID&nbsp;
                        <span v-if="stateBatch">{{ formatStateID(potentialMatch.id) }}</span>
                        <span v-else>{{ potentialMatch.id }}</span>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div
            v-if="isPending"
            class="field is-grouped is-flex flex-justify--end"
            style="padding-top: 1rem"
          >
            <div
              v-if="hasDistrictSchoolPermission(matchPermission('DRMCH:MATCH:RESOLVENM:CANCEL'))"
              class="control is-flex flex-align--center"
            >
              <a @click="openModalForCancel()">Cancel Submission Record</a>
            </div>
            <div
              v-if="hasDistrictSchoolPermission(matchPermission('DRMCH:MATCH:RESOLVENM:NOMATCH'))"
              class="control"
            >
              <button
                class="button is-link is-light"
                @click="openModalForNoMatch()"
              >
                {{ properties['BUTTON_LABEL-CREATE_NEW'] }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
  .comma + .comma:before
    content: ",  "
  .panel--highlighted
    background-color: rgb(255, 245, 214)
  .border--left
    border-left: 1px solid #ccc
  .border--bottom
    border-bottom: 1px solid #ccc
  .padding--1rem
    padding: 1rem
  .margin-bottom
    margin-bottom: 1rem
  .margin-left
    margin-left: 1rem
  .margin-bottom--half
    margin-bottom: 0.5rem
  .submission-header
    font-size: 12px
    color: #7A7A7A
  .compare-panel
    padding: 1rem
    min-width: 320px
    max-width: 320px
    // flex-wrap: wrap
  .compare-panel--cell
    padding: 0.5rem
    // border: 1px solid grey
  .compare-panel--heading
    background-color: #ccc !important
    // margin: 0 1rem
    padding: 0.5rem
  .compare--content
    font-size: 16px
    font-weight: bold
    text-align: left
  .compare-panel--summary
    flex: 1 0 auto
  .stateImage
    display: block
    margin-left: auto
    margin-right: auto
  .thing
    min-width: 300px
    max-width: 350px

  .test
    // flex-wrap: wrap
    overflow: hidden
    white-space: nowrap

  .source-label
    margin-top: 6px
    background-color: #ccc
    display: inline-block
    padding: 5px 5px 5px 5px

  .percent-label
    font-style: bold
    color: black
    font-size: 1.1rem
    font-weight: 800
    padding: .25rem 0 .25rem .15rem

  table
    border-collapse: separate
    tbody
      td:first-child
        position: sticky
        left: 0
        background-color: #fff
        z-index: 1
      tr:last-child td
          border-bottom-width: 1px

  th
    vertical-align: top
    border-top: 1px solid #ccc

  td
    min-width: 320px
    max-width: 320px

  .outer
    // position: relative
    // margin-left: auto
    padding: 1rem 0
    display: flex
    justify-content: center
  .inner
    overflow-x: auto
    overflow-y: visible
    // margin-left: 320px
    max-width: max-content
  .modal-card-title
    display: flex
  .modal-body
    margin-bottom: 1rem
    display: flex
    justify-content: flex-start


  .text-align--center
    text-align: center
  .vertical-align--initial
    vertical-align: initial
  .vertical-align--bottom
    vertical-align: bottom
  .list-content
    height: 100%
    display: flex
    flex-direction: column
    justify-content: center
    .list-header
      .list-title
        font-size: 1.15rem
        color: #000
        font-weight: 700
      .list-subtitle
        font-size: .85rem
        color: #666
  .assigned-state-id
    border-top: 2px solid rgb(169, 209, 142)
    border-right: 4px solid rgb(169, 209, 142)
    border-bottom: 2px solid rgb(169, 209, 142)
    border-left: 4px solid rgb(169, 209, 142)
    box-shadow: inset 15px 0 15px -15px #999, 15px 0 15px -15px #999
  .capitalize
    text-transform: capitalize

</style>
